// Coperți automate: caută → alege cea mai bună → descarcă → micșorează → salvează în D1 → leagă de carte.
import type { Work } from '../../shared/types';
import { apiJson } from './http';
import { blobToBitmap, fetchRemoteImage, fullCrop, uploadCover } from './images';
import { updateWork } from './actions';

export interface Candidate { url: string; thumb?: string; source: string; score: number; ro: boolean }

// Căutarea combină: serverul (librării RO / okazii / Bing / DuckDuckGo) + telefonul direct
// (Google Books și Open Library răspund mai bine la IP-ul telefonului decât la serverele Cloudflare).
export async function searchCandidates(w: Pick<Work, 'title' | 'authors' | 'isbn'>, fresh = false): Promise<Candidate[]> {
  const q = new URLSearchParams({ title: w.title || '', author: (w.authors || '').split(';')[0] || '', isbn: w.isbn || '' });
  if (fresh) q.set('fresh', '1');
  const [server, local] = await Promise.all([
    apiJson<{ candidates: Candidate[] }>(`/api/covers/search?${q}`).then(r => r.candidates || []).catch(() => [] as Candidate[]),
    clientSearch(w).catch(() => [] as Candidate[])
  ]);
  const seen = new Set<string>();
  return [...server, ...local].filter(c => !seen.has(c.url) && seen.add(c.url)).sort((a, b) => b.score - a.score).slice(0, 15);
}

const words = (s: string) => new Set(s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').split(/\W+/).filter(x => x.length > 2));
const sim = (a: string, b: string) => { const A = words(a), B = words(b); if (!A.size || !B.size) return 0; let n = 0; A.forEach(x => B.has(x) && n++); return n / A.size; };

async function clientSearch(w: Pick<Work, 'title' | 'authors' | 'isbn'>): Promise<Candidate[]> {
  const title = (w.title || '').replace(/\(.*?\)|\[.*?\]/g, '').trim();
  const author = (w.authors || '').split(';')[0].trim();
  const isbn = (w.isbn || '').replace(/[^0-9X]/gi, '');
  const out: Candidate[] = [];
  const ctrl = AbortSignal.timeout ? AbortSignal.timeout(5000) : undefined;
  const gq = isbn ? `isbn:${isbn}` : `intitle:${title}${author ? ` inauthor:${author}` : ''}`;
  const [gb, ol] = await Promise.all([
    fetch(`https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(gq)}&maxResults=8&printType=books`, { signal: ctrl }).then(r => r.ok ? r.json() : {}).catch(() => ({})),
    fetch(`https://openlibrary.org/search.json?${isbn ? `isbn=${isbn}` : `title=${encodeURIComponent(title)}${author ? `&author=${encodeURIComponent(author)}` : ''}`}&limit=5&fields=title,cover_i,language`, { signal: ctrl }).then(r => r.ok ? r.json() : {}).catch(() => ({}))
  ]);
  ((gb as any).items || []).forEach((it: any, i: number) => {
    const v = it.volumeInfo || {}; const l = v.imageLinks || {};
    const u = l.extraLarge || l.large || l.medium || l.small || l.thumbnail;
    if (!u) return;
    const ro = v.language === 'ro';
    out.push({ url: String(u).replace(/^http:/, 'https:').replace(/&edge=curl/g, ''), source: 'Google Books', ro, score: (isbn ? 60 : 30) + (ro ? 20 : 0) + sim(v.title || '', title) * 20 - i * 2 });
  });
  ((ol as any).docs || []).forEach((d: any, i: number) => {
    if (!d.cover_i) return;
    const ro = (d.language || []).some((x: string) => x === 'rum' || x === 'ron');
    out.push({ url: `https://covers.openlibrary.org/b/id/${d.cover_i}-L.jpg?default=false`, thumb: `https://covers.openlibrary.org/b/id/${d.cover_i}-M.jpg`, source: 'Open Library', ro, score: (isbn ? 50 : 20) + (ro ? 20 : 0) + sim(d.title || '', title) * 15 - i * 2 });
  });
  return out;
}

// Date după ISBN direct de pe telefon (dacă serverul n-a găsit nimic)
export async function clientLookupIsbn(isbn: string) {
  const r = await fetch(`https://www.googleapis.com/books/v1/volumes?q=isbn:${isbn}`).then(r => r.ok ? r.json() : {}).catch(() => ({}));
  const v = (r as any).items?.[0]?.volumeInfo;
  if (!v) return null;
  return { title: [v.title, v.subtitle].filter(Boolean).join(': '), authors: (v.authors || []).join('; '), publisher: v.publisher || null, year: v.publishedDate ? Number(String(v.publishedDate).slice(0, 4)) || null : null, synopsis: v.description || '' };
}

// Verifică dacă imaginea arată a copertă (portret, nu minusculă) și o salvează
export async function storeFromUrl(workId: string, url: string, source: 'auto' | 'manual' | 'legacy' | 'verified'): Promise<string> {
  const blob = await fetchRemoteImage(url);
  const bmp = await blobToBitmap(blob);
  if (bmp.width < 100 || bmp.height < 140) throw new Error('Imagine prea mică');
  const ratio = bmp.width / bmp.height;
  if (source === 'auto' && (ratio < 0.4 || ratio > 1.0)) throw new Error('Nu pare o copertă (proporții)');
  const local = await uploadCover(bmp, fullCrop(bmp), { workId, source, sourceUrl: url });
  await updateWork(workId, { cover_url: local, cover_source: source });
  return local;
}

export async function autoCover(w: Work): Promise<{ ok: boolean; source?: string }> {
  const cands = await searchCandidates(w);
  for (const c of cands.slice(0, 5)) {
    try { await storeFromUrl(w.id, c.url, 'auto'); return { ok: true, source: c.source }; } catch { /* încercăm următorul */ }
  }
  return { ok: false };
}

export const isLocalCover = (u?: string | null) => !!u && u.startsWith('/img/');
